import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, of, Subscription, merge, forkJoin } from 'rxjs';
import { finalize, map, catchError, switchMap, filter, share, tap } from 'rxjs/operators';
import { UserAccount, UserAccountType } from '../models/user.model';
import { AuthenticationRequest, AuthenticationResult, StsTokenOutput } from '../models/auth.model';
import { AuthHTTPService } from './auth-http';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { DataEncryptionService } from './data-encryption.service';
import { ResetPasswordRequest, ResetPasswordResult } from '../models/password.model';
import { IImpersonatedAuthenticationResponseModelData, ImpersonatedAuthenticationResponseModel } from '../models/impersonation.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  // public fields
  private isLoadingSubject: BehaviorSubject<boolean>;
  private currentUserSubject: BehaviorSubject<UserAccountType>;
  private permissionListSubject: BehaviorSubject<string[]>;
  private previousUrl: string = '';
  private isLoggingOut = false; // To prevent multiple logout requests

  get isLoading$(): Observable<boolean> {
    return this.isLoadingSubject.asObservable();
  }

  get currentUser$(): Observable<UserAccountType> {
    return this.currentUserSubject.asObservable();
  }

  get currentUserValue(): UserAccountType {
    return this.currentUserSubject.value;
  }

  get permissionList$(): Observable<string[]> {
    return this.permissionListSubject.asObservable();
  }

  set currentUserValue(user: UserAccountType) {
    this.currentUserSubject.next(user);
  }

  set currentTenantIdValue(tenantId: number | null) {
    this.setTenantInCookie(tenantId);
  }

  get isAuthenticated(): boolean {
    return this.cookieService.check(environment.tokenKey);
  }

  get token(): string | null {
    return this.cookieService.get(environment.tokenKey);
  }

  get lastStoredUsername(): string {
    return localStorage.getItem(environment.lastStoredUsernameKey) ?? '';
  }

  constructor(
    private authHttpService: AuthHTTPService,
    private cookieService: CookieService,
    private encryptionService: DataEncryptionService,
    private router: Router
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserAccountType>(undefined);
    this.permissionListSubject = new BehaviorSubject<string[]>([]);
  }

  // public methods
  login(request: AuthenticationRequest): Observable<UserAccountType> {
    this.isLoadingSubject.next(true);
    let login = this.authHttpService.login(request)
      .pipe(
        share()
      );

    let resetPassword = login
      .pipe(
        filter((result: AuthenticationResult) => result.shouldResetPassword),
        tap((result: AuthenticationResult) => {
          // If shouldResetPassword is true, redirect first to reset password page
          this.router.navigate(['/account/reset-password'], {
            queryParams: { resetCode: result.passwordResetCode, userId: result.userId, tenantId: result.tenantId }
          });
        }),
        map((result: AuthenticationResult) => {
          let user = new UserAccount();
          // Return userId only if shouldResetPassword flag is set to true
          user.id = result.userId;
          return user;
        }),
        finalize(() => this.isLoadingSubject.next(false))
      );

    let authenticated = login
      .pipe(
        filter((result: AuthenticationResult) => !result.shouldResetPassword),
        tap((result: AuthenticationResult) => {
          if (!result.accessToken) {
            throw new Error("Failed to authenticate");
          }

          if (result.tenantId != null) {
            this.router.navigate(['/account/login']);
            throw new Error("You are not allowed access this site");
          }

          // Store tokens in cookie
          this.setAuthFromCookies(result);

          // Store username if remember me is enabled
          if (request.rememberClient) {
            this.storeUsernameInLocal(request.userNameOrEmailAddress, 'loginKey');
          }
          else {
            this.deleteLastStoredUsernameFromLocal();
          }
        }),
        switchMap(() => this.getCurrentUser()),
        finalize(() => this.isLoadingSubject.next(false))
      );

    return merge(resetPassword, authenticated);
  }

  logout(reload?: boolean): Observable<any> {
    if (reload !== false) {
      return this.httpLogout().pipe(
        tap(() => {
          this.clearCookies();
          this.clearSearchStealthColumnKeys();
          this.router.navigate(['/account/login'], { queryParams: {} }).then(() => {
            document.location.reload();
        });
        })
      );
    } else {
      return this.httpLogout().pipe(
        tap(() => {
          this.clearCookies();
          this.clearSearchStealthColumnKeys();
        })
      );
    }
  }

  private httpLogout(): Observable<any> {
    if (this.isLoggingOut || !this.token) return of(null);
    this.isLoggingOut = true;
    return this.authHttpService.logout(this.token).pipe(
      finalize(() => this.isLoggingOut = false)
    );
  }

  private clearCookies() {
    this.cookieService.delete(environment.tokenKey, '/');
    this.cookieService.delete(environment.encryptedTokenKey, '/');
    this.cookieService.delete(environment.tenantIdKey, '/');
    this.cookieService.delete(environment.userIdKey, '/');
  }

  private clearSearchStealthColumnKeys() {
    const prefix = 'searchStealthColumns';

    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    }
  }

  getCurrentUser(): Observable<UserAccountType> {
    if (!this.token) {
      this.currentUserSubject.next(undefined);
      this.clearSearchStealthColumnKeys();
      return of(undefined);
    }

    this.isLoadingSubject.next(true);
    return forkJoin({
      user: this.authHttpService.getCurrentUserDetails(),
      sessionData: this.authHttpService.getSessionData()
    }).pipe(
      map(({ user, sessionData }) => {
        if (!user) {
          this.currentUserSubject.next(null);
          return user;
        }
        this.permissionListSubject.next(this.parsePermissions(sessionData.auth.allPermissions));
        user.permissions = this.parsePermissions(sessionData.auth.grantedPermissions);
        user.isImpersonated = sessionData.session.impersonatorUserId != null;
        this.currentUserSubject.next(user);
        return user;
      }),
      catchError((error: any) => {
        this.currentUserSubject.next(null);
        throw error;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  private parsePermissions(permissions: any): string[] {
    let parsedPermissions: string[] = [];

    if (!permissions)
      return parsedPermissions;

    for (const key of Object.keys(permissions)) {
      if (permissions[key]) {
        parsedPermissions.push(key);
      };
    }
    return parsedPermissions;
  }

  forgotPassword(email: string): Observable<void> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.forgotPassword(email)
      .pipe(
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  resolveAndUpdateTenant(code: string): Observable<number> {
    return this.authHttpService.resolveTenant(code)
      .pipe(
        map((tenantId: number) => {
          this.currentTenantIdValue = tenantId;
          return this.currentTenantIdValue;
        })
      );
  }

  resetPassword(request: ResetPasswordRequest): Observable<ResetPasswordResult> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.resetPassword(request)
      .pipe(
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  impersonatedLogin(token: string) {
    return this.authHttpService.impersonatedLogin(token).pipe(
      map((response: IImpersonatedAuthenticationResponseModelData) => {
        if (!response) {
          throw Error("Failed to impersonate");
        }

        document.location.search = '';

        this.setImpersonatedAuthInLocalStorage(response.result);
        return response.result;
      }),
      switchMap(() => this.getCurrentUser()),
      finalize(() => { }));
  }

  callSts(azureToken: string): Observable<StsTokenOutput> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.callSts(azureToken)
      .pipe(
      finalize(() => this.isLoadingSubject.next(false))
      );
  }

  remoteLogin(token: string, expiresIn: number): void {
    console.log('remoteLogin called with token:', token ? 'Yes' : 'No');
    
    // Store token in cookies
    const expiry = expiresIn / 86400; // Convert to days
    this.cookieService.set(environment.tokenKey, token, expiry, '/');
    
    console.log('Token stored, getting current user...');
    
    // Get user data
    this.getCurrentUser().subscribe({
      next: (user) => {
        console.log('User loaded:', user);
      },
      error: (error) => {
        console.error('Failed to load user:', error);
      }
    });
  }

  remoteLoginV2(token: string, expiresIn: number): Observable<UserAccountType> {
    
    // Store token in cookies
    const expiry = expiresIn / 86400; // Convert to days
    this.cookieService.set(environment.tokenKey, token, expiry, '/');
    
    console.log('Token stored, getting current user...');
    
    // Return the observable so caller can wait for user data
    return this.getCurrentUser().pipe(
      tap(user => console.log('User loaded in remoteLoginV2:', user)),
      catchError(error => {
        console.error('Failed to load user in remoteLoginV2:', error);
        throw error;
      })
    );
  }

  setPreviousUrl(url: string): void {
    this.previousUrl = url;
  }

  // private methods
  private setAuthFromCookies(auth: any): boolean {
    // store auth authToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
    if (auth) {
      const expiry = auth.expireInSeconds / 86400;
      this.cookieService.set(environment.tokenKey, auth.accessToken, expiry, "/");
      this.cookieService.set(environment.encryptedTokenKey, auth.encryptedAccessToken, expiry, "/");
      this.cookieService.set(environment.userIdKey, auth.userId.toString(), expiry, "/");
      this.setTenantInCookie(auth.tenantId ?? 0, expiry);
      return true;
    }
    return false;
  }

  private setTenantInCookie(tenantId: number | null, expiry?: number) {
    this.cookieService.set(environment.tenantIdKey, tenantId?.toString() ?? '', expiry, "/");
  }

  private storeUsernameInLocal(username: string, key: string) {
    const encryptedUsername = this.encryptionService.encrypt(username, key) ?? '';
    localStorage.setItem(environment.lastStoredUsernameKey, encryptedUsername);
  }

  private deleteLastStoredUsernameFromLocal() {
    localStorage.removeItem(environment.lastStoredUsernameKey);
  }

  private setImpersonatedAuthInLocalStorage(response: ImpersonatedAuthenticationResponseModel): void {
    const expiry = response.expireInSeconds / 86400;
    this.cookieService.set(environment.tokenKey, response.accessToken, expiry, '/');
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
